import { FC } from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Badge from '@mui/material/Badge';
import ArrowForward from '@mui/icons-material/ArrowForward';
import { styled, useTheme } from '@mui/material/styles';
import { Row } from 'further-ui/components/Layout';

const Message = styled(Row)(({ theme }) => ({
  ...theme.typography.body2,
  width: '100%',
  padding: theme.spacing(theme.spacingSizes.xs, theme.spacingSizes.xl),
  color: theme.palette.primary.contrastText,
  textAlign: 'center',
  borderRadius: '8px',
}));

const MessageLink = styled(Link)(({ theme }) => ({
  textDecoration: 'none',
  color: theme.palette.primary.contrastText,
}));

const StyledBadge = styled(Badge)(({ theme }) => ({
  marginRight: theme.spacing(theme.spacingSizes.xxs),
}));

type Props = {
  message: string;
  to?: string;
  color?: 'success' | 'default';
  openInNewTab?: boolean;
  requiresAction?: boolean;
};

const Banner: FC<Props> = ({
  message,
  to,
  color = 'default',
  openInNewTab = false,
  requiresAction = false,
}) => {
  const theme = useTheme();

  const colorMap = {
    success: {
      background: theme.palette.primary.main,
      icon: theme.palette.primary.contrastText,
    },
    default: {
      background: theme.palette.grey[900],
      icon: theme.palette.grey[500],
    },
  };

  const content = (
    <Row centered spacing="xs">
      <StyledBadge variant="dot" color="warning" invisible={!requiresAction} />
      <Box>{message}</Box>
      {to ? (
        <ArrowForward
          fontSize="small"
          sx={{
            color: colorMap[color].icon,
          }}
        />
      ) : null}
    </Row>
  );

  return (
    <Message justify="center" sx={{ background: colorMap[color].background }}>
      {to ? (
        <MessageLink to={to} {...(openInNewTab ? { target: '_blank' } : {})}>
          {content}
        </MessageLink>
      ) : (
        content
      )}
    </Message>
  );
};

export default Banner;
